import React, { useState, useEffect, useCallback } from 'react';
import { Truck, Calculator, Package, TrendingUp, Shovel, Construction, Minus, Cylinder, Building2, Trash2, Plus, Settings, X, Check, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { truckDatabase } from '../data/trucks';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

const TruckingCalculator = ({ 
  areaTotals, 
  pipingTotals, 
  tanksTotals, 
  buildingsTotals, 
  bulkTotals, 
  excavationTotals, 
  customImportTotals = { totalTons: 0, totalCubicYards: 0, itemCount: 0 },
  customExcavationTotals = { totalTons: 0, totalCubicYards: 0, itemCount: 0 },
  truckingCalculations, 
  setTruckingCalculations 
}) => {
  const [selectedSource, setSelectedSource] = useState('area');
  const [selectedTruck, setSelectedTruck] = useState('');
  const [haulingCostPerTon, setHaulingCostPerTon] = useState('');
  const [disposalCostPerTon, setDisposalCostPerTon] = useState('');
  const [disposalSiteName, setDisposalSiteName] = useState('');
  const calculations = truckingCalculations || [];
  const setCalculations = setTruckingCalculations || (() => {});

  // Custom trucks management
  const [customTrucks, setCustomTrucks] = useState(() => {
    const saved = localStorage.getItem('customTrucks');
    return saved ? JSON.parse(saved) : [];
  });
  const [showTruckForm, setShowTruckForm] = useState(false);
  const [newTruck, setNewTruck] = useState({
    name: '',
    category: 'Custom Trucks',
    capacityTons: '',
    capacityCubicYards: '',
    description: ''
  });

  // Save custom trucks to localStorage
  useEffect(() => {
    localStorage.setItem('customTrucks', JSON.stringify(customTrucks));
  }, [customTrucks]);

  // Helper to generate unique IDs
  const generateId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Combine default and custom trucks
  const allTrucks = [...truckDatabase, ...customTrucks];

  const addCustomTruck = () => {
    if (!newTruck.name.trim()) {
      toast.error('Please enter a truck name');
      return;
    }
    if (!newTruck.capacityTons || parseFloat(newTruck.capacityTons) <= 0) {
      toast.error('Please enter a valid weight capacity');
      return;
    }
    const truck = {
      id: `custom_${Date.now()}`,
      name: newTruck.name,
      category: newTruck.category || 'Custom Trucks',
      capacityTons: parseFloat(newTruck.capacityTons),
      capacityCubicYards: parseFloat(newTruck.capacityCubicYards) || null,
      description: newTruck.description || 'Custom truck',
      isCustom: true
    };
    setCustomTrucks([...customTrucks, truck]);
    setNewTruck({ name: '', category: 'Custom Trucks', capacityTons: '', capacityCubicYards: '', description: '' });
    setShowTruckForm(false);
    toast.success(`Truck "${truck.name}" added`);
  };

  const removeCustomTruck = (id) => {
    setCustomTrucks(customTrucks.filter(t => t.id !== id));
    toast.success('Truck removed');
  };

  const getCurrentTotals = () => {
    switch(selectedSource) {
      case 'area':
        return areaTotals;
      case 'piping':
        return pipingTotals;
      case 'tanks':
        return tanksTotals;
      case 'buildings':
        return buildingsTotals;
      case 'bulk':
        return bulkTotals;
      case 'excavation':
        return excavationTotals;
      case 'customImport':
        return customImportTotals;
      case 'customExcavation':
        return customExcavationTotals;
      default:
        return areaTotals;
    }
  };

  const currentTotals = getCurrentTotals();
  const selectedTruckData = allTrucks.find(t => t.name === selectedTruck);

  const calculateTrucksAndDisposal = () => {
    if (!selectedTruckData) return;

    const totalTons = selectedSource === 'area' 
      ? currentTotals.totalWeight / 2000 
      : selectedSource === 'piping'
      ? currentTotals.totalWeight / 2000
      : selectedSource === 'tanks'
      ? currentTotals.totalWeight / 2000
      : selectedSource === 'buildings'
      ? currentTotals.totalTons
      : currentTotals.totalTons;

    const totalCubicYards = selectedSource === 'bulk' 
      ? currentTotals.totalCubicYards 
      : selectedSource === 'excavation'
      ? currentTotals.totalLooseCY
      : selectedSource === 'customImport' || selectedSource === 'customExcavation'
      ? currentTotals.totalCubicYards
      : null;

    let trucksNeeded = 0;
    let calculationMethod = '';

    // Calculate based on weight (tons)
    const trucksByWeight = Math.ceil(totalTons / selectedTruckData.capacityTons);
    trucksNeeded = trucksByWeight;
    calculationMethod = 'weight';

    // If bulk materials and truck has cubic yard capacity, also calculate by volume
    if ((selectedSource === 'bulk' || selectedSource === 'customImport' || selectedSource === 'customExcavation') && selectedTruckData.capacityCubicYards && totalCubicYards) {
      const trucksByVolume = Math.ceil(totalCubicYards / selectedTruckData.capacityCubicYards);
      // Use whichever requires more trucks (limiting factor)
      if (trucksByVolume > trucksByWeight) {
        trucksNeeded = trucksByVolume;
        calculationMethod = 'volume';
      }
    }

    const getSourceName = (source) => {
      if (source === 'area') return 'Demo Materials';
      if (source === 'piping') return 'Oil Field Piping';
      if (source === 'tanks') return 'Oil Field Tanks';
      if (source === 'buildings') return 'Custom Buildings';
      if (source === 'bulk') return 'Import';
      if (source === 'excavation') return 'Excavation';
      if (source === 'customImport') return 'Custom Import Volume';
      if (source === 'customExcavation') return 'Custom Excavation Volume';
      return source;
    };

    const costPerTonHauling = parseFloat(haulingCostPerTon) || 0;
    const costPerTonDisposal = parseFloat(disposalCostPerTon) || 0;
    const totalHaulingCost = totalTons * costPerTonHauling;
    const totalDisposalCost = totalTons * costPerTonDisposal;

    const newCalculation = {
      id: generateId(),
      source: selectedSource,
      sourceName: getSourceName(selectedSource),
      truckType: selectedTruckData.name,
      truckCategory: selectedTruckData.category,
      truckCapacityTons: selectedTruckData.capacityTons,
      truckCapacityCY: selectedTruckData.capacityCubicYards,
      totalTons: totalTons,
      totalCubicYards: totalCubicYards,
      trucksNeeded: trucksNeeded,
      calculationMethod: calculationMethod,
      loadsPerTruck: (totalTons / trucksNeeded).toFixed(2),
      haulingCostPerTon: costPerTonHauling,
      totalHaulingCost: totalHaulingCost,
      disposalCostPerTon: costPerTonDisposal,
      totalDisposalCost: totalDisposalCost,
      disposalSite: disposalSiteName,
      totalCost: totalHaulingCost + totalDisposalCost
    };

    setCalculations([...calculations, newCalculation]);
    setSelectedTruck('');
    setHaulingCostPerTon('');
    setDisposalCostPerTon('');
    setDisposalSiteName('');
    toast.success('T&D calculation added!');
  };

  const removeCalculation = (id) => {
    setCalculations(calculations.filter(c => c.id !== id));
  };

  const clearAll = () => {
    setCalculations([]);
  };

  const groupedTrucks = allTrucks.reduce((acc, truck) => {
    const category = truck.category || 'Custom Trucks';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(truck);
    return acc;
  }, {});

  const getTotalTrucks = () => {
    return calculations.reduce((sum, calc) => sum + calc.trucksNeeded, 0);
  };

  const getTotalHaulingCost = () => {
    return calculations.reduce((sum, calc) => sum + (calc.totalHaulingCost || 0), 0);
  };

  const getTotalDisposalCost = () => {
    return calculations.reduce((sum, calc) => sum + (calc.totalDisposalCost || 0), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="space-y-8">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Source</span>
            <Construction className="w-5 h-5 text-primary" />
          </div>
          <p className="text-2xl font-bold text-foreground">
            {selectedSource === 'area' ? 'Demo Materials' : 
             selectedSource === 'piping' ? 'Oil Field Piping' :
             selectedSource === 'tanks' ? 'Oil Field Tanks' :
             selectedSource === 'buildings' ? 'Custom Buildings' :
             selectedSource === 'bulk' ? 'Import' : 
             selectedSource === 'customImport' ? 'Custom Import Volume' :
             selectedSource === 'customExcavation' ? 'Custom Excavation Volume' : 'Excavation'}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {currentTotals.itemCount} items
          </p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Total Trucks</span>
            <Truck className="w-5 h-5 text-success" />
          </div>
          <p className="text-3xl font-bold text-foreground">{getTotalTrucks()}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {calculations.length} calculations
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Trucking Cost</span>
            <Truck className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(getTotalHaulingCost())}</p>
          <p className="text-sm text-muted-foreground mt-1">Transportation</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Disposal Cost</span>
            <Trash2 className="w-5 h-5 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-orange-600">{formatCurrency(getTotalDisposalCost())}</p>
          <p className="text-sm text-muted-foreground mt-1">Landfill/Dump fees</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: T&D Calculator */}
        <div className="lg:col-span-1">
          <Card className="border border-border shadow-card bg-card sticky top-4">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Calculate Trucks & Disposal
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="text-sm">Calculate trucking and disposal costs. Select a material source, truck type, and enter hauling/disposal costs per ton. The calculator computes total trips needed based on truck capacity.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Settings className="w-4 h-4 mr-1" />
                      Manage
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Manage Custom Trucks</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {showTruckForm ? (
                        <Card className="bg-muted/30">
                          <CardContent className="pt-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Truck Name</Label>
                                <Input
                                  value={newTruck.name}
                                  onChange={(e) => setNewTruck({...newTruck, name: e.target.value})}
                                  placeholder="e.g., My Custom Hauler"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Category</Label>
                                <Input
                                  value={newTruck.category}
                                  onChange={(e) => setNewTruck({...newTruck, category: e.target.value})}
                                  placeholder="Custom Trucks"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Weight Capacity (tons) *</Label>
                                <Input
                                  type="number"
                                  value={newTruck.capacityTons}
                                  onChange={(e) => setNewTruck({...newTruck, capacityTons: e.target.value})}
                                  placeholder="e.g., 15"
                                  className="h-8 text-sm"
                                  min="0"
                                  step="0.1"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Volume Capacity (cy)</Label>
                                <Input
                                  type="number"
                                  value={newTruck.capacityCubicYards}
                                  onChange={(e) => setNewTruck({...newTruck, capacityCubicYards: e.target.value})}
                                  placeholder="e.g., 18"
                                  className="h-8 text-sm"
                                  min="0"
                                  step="0.1"
                                />
                              </div>
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs">Description</Label>
                                <Input
                                  value={newTruck.description}
                                  onChange={(e) => setNewTruck({...newTruck, description: e.target.value})}
                                  placeholder="Optional description"
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" onClick={addCustomTruck}>
                                <Check className="w-3 h-3 mr-1" />
                                Add Truck
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setShowTruckForm(false)}>
                                Cancel
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setShowTruckForm(true)} className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Add New Custom Truck
                        </Button>
                      )}

                      {customTrucks.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Your Custom Trucks</Label>
                          {customTrucks.map(truck => (
                            <div key={truck.id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                              <Truck className="w-4 h-4 text-muted-foreground" />
                              <span className="flex-1 text-sm">{truck.name}</span>
                              <span className="text-xs font-medium text-primary">{truck.capacityTons} tons</span>
                              {truck.capacityCubicYards && (
                                <span className="text-xs text-muted-foreground">/ {truck.capacityCubicYards} cy</span>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => removeCustomTruck(truck.id)} className="h-7 px-2">
                                <X className="w-3 h-3 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg">
                        <p>Add custom trucks with their weight capacity to use in calculations.</p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="source" className="text-sm font-medium text-foreground">
                  Material Source
                </Label>
                <Select value={selectedSource} onValueChange={setSelectedSource}>
                  <SelectTrigger id="source" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="area">
                      <div className="flex items-center gap-2">
                        <Construction className="w-4 h-4" />
                        <span>Demo Materials (sq ft)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="piping">
                      <div className="flex items-center gap-2">
                        <Minus className="w-4 h-4 rotate-90" />
                        <span>Oil Field Piping (linear ft)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="tanks">
                      <div className="flex items-center gap-2">
                        <Cylinder className="w-4 h-4" />
                        <span>Oil Field Tanks (units)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="buildings">
                      <div className="flex items-center gap-2">
                        <Construction className="w-4 h-4" />
                        <span>Custom Buildings</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="bulk">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 scale-x-[-1]" />
                        <span>Import (cy)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="excavation">
                      <div className="flex items-center gap-2">
                        <Shovel className="w-4 h-4" />
                        <span>Excavation (BCY/LCY)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="customImport">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        <span>Custom Import Volume (L×W×H)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="customExcavation">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        <span>Custom Excavation Volume (L×W×H)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {currentTotals.itemCount === 0 ? (
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    No materials in selected calculator.
                    <br />Please add materials first.
                  </p>
                </div>
              ) : (
                <>
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Weight:</span>
                      <span className="font-semibold text-foreground">
                        {(selectedSource === 'area' || selectedSource === 'piping' || selectedSource === 'tanks')
                          ? ((currentTotals.totalWeight || 0) / 2000).toFixed(2) 
                          : ((currentTotals.totalTons || 0)).toFixed(2)
                        } tons
                      </span>
                    </div>
                    {(selectedSource === 'bulk' || selectedSource === 'excavation' || selectedSource === 'customImport' || selectedSource === 'customExcavation') && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total Volume:</span>
                        <span className="font-semibold text-foreground">
                          {(selectedSource === 'excavation' ? currentTotals.totalLooseCY : currentTotals.totalCubicYards || 0).toFixed(2)} cy
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="truck" className="text-sm font-medium text-foreground">
                      Truck Type
                    </Label>
                    <Select value={selectedTruck} onValueChange={setSelectedTruck}>
                      <SelectTrigger id="truck" className="w-full">
                        <SelectValue placeholder="Select a truck" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[400px]">
                        {Object.entries(groupedTrucks).map(([category, trucks]) => (
                          <div key={category}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                              {category}
                            </div>
                            {trucks.map(truck => (
                              <SelectItem key={truck.name} value={truck.name}>
                                <div className="flex items-center justify-between w-full pr-4">
                                  <span className="text-sm">{truck.name}</span>
                                  <span className="text-xs text-muted-foreground ml-2">
                                    {truck.capacityTons} tons
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedTruckData && (
                    <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Capacity:</span>
                        <span className="font-semibold text-primary">{selectedTruckData.capacityTons} tons</span>
                      </div>
                      {selectedTruckData.capacityCubicYards && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Volume:</span>
                          <span className="font-semibold text-primary">{selectedTruckData.capacityCubicYards} cy</span>
                        </div>
                      )}
                    </div>
                  )}

                  <Separator />

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Truck className="w-4 h-4 text-blue-500" />
                      Trucking Costs
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="haulingCost" className="text-xs text-muted-foreground">
                        Hauling Cost per Ton
                      </Label>
                      <Input
                        id="haulingCost"
                        type="number"
                        placeholder="$0.00"
                        value={haulingCostPerTon}
                        onChange={(e) => setHaulingCostPerTon(e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Trash2 className="w-4 h-4 text-orange-500" />
                      Disposal Costs
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="disposalSite" className="text-xs text-muted-foreground">
                        Disposal Site Name (Optional)
                      </Label>
                      <Input
                        id="disposalSite"
                        type="text"
                        placeholder="e.g., County Landfill"
                        value={disposalSiteName}
                        onChange={(e) => setDisposalSiteName(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="disposalCost" className="text-xs text-muted-foreground">
                        Disposal Cost per Ton
                      </Label>
                      <Input
                        id="disposalCost"
                        type="number"
                        placeholder="$0.00"
                        value={disposalCostPerTon}
                        onChange={(e) => setDisposalCostPerTon(e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={calculateTrucksAndDisposal}
                    disabled={!selectedTruck}
                    className="w-full bg-primary hover:bg-primary/90 mt-4"
                    size="lg"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate Trucks & Disposal
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Middle + Right Column: Results */}
        <div className="lg:col-span-2 space-y-4">
          {calculations.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Truck className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Calculations Yet</h3>
                <p className="text-muted-foreground">
                  Select a material source, choose a truck type, and calculate to see results here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">T&D Calculations ({calculations.length})</h3>
                <Button variant="outline" size="sm" onClick={clearAll}>
                  Clear All
                </Button>
              </div>
              
              {calculations.map((calc, index) => (
                <Card key={calc.id} className="border-l-4 border-l-primary">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold">{calc.sourceName}</h4>
                        <p className="text-sm text-muted-foreground">{calc.truckType}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{calc.trucksNeeded} trucks</Badge>
                        <Button variant="ghost" size="sm" onClick={() => removeCalculation(calc.id)}>
                          <X className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total Weight</p>
                        <p className="font-semibold">{calc.totalTons?.toFixed(2)} tons</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Truck Capacity</p>
                        <p className="font-semibold">{calc.truckCapacityTons} tons</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground flex items-center gap-1">
                          <Truck className="w-3 h-3 text-blue-500" />
                          Trucking
                        </p>
                        <p className="font-semibold text-blue-600">{formatCurrency(calc.totalHaulingCost)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground flex items-center gap-1">
                          <Trash2 className="w-3 h-3 text-orange-500" />
                          Disposal
                        </p>
                        <p className="font-semibold text-orange-600">{formatCurrency(calc.totalDisposalCost)}</p>
                      </div>
                    </div>

                    {calc.disposalSite && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Disposal Site: {calc.disposalSite}
                      </p>
                    )}

                    <div className="mt-3 pt-3 border-t flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">T&D Total</span>
                      <span className="text-lg font-bold text-success">{formatCurrency(calc.totalCost)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Summary Card */}
              <Card className="bg-gradient-to-r from-primary/10 to-success/10">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-4">T&D Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Trucks</p>
                      <p className="text-2xl font-bold">{getTotalTrucks()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Truck className="w-3 h-3 text-blue-500" />
                        Total Trucking
                      </p>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(getTotalHaulingCost())}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Trash2 className="w-3 h-3 text-orange-500" />
                        Total Disposal
                      </p>
                      <p className="text-2xl font-bold text-orange-600">{formatCurrency(getTotalDisposalCost())}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Grand Total</p>
                      <p className="text-2xl font-bold text-success">{formatCurrency(getTotalHaulingCost() + getTotalDisposalCost())}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TruckingCalculator;
